import { DomClassList, EventStore, ResourceTimeRangeModel } from "@bryntum/schedulerpro"

import {
    EventModel,
    JobTimelineScheduler,
    OverlappingRegion,
    SchedulerEventModel,
    SchedulerResourceModel,
} from "@pages/Jobs/JobList/views/JobTimelineView/JobTimelineView.types"

import useJobTimelineViewBryntumInstances from "./useJobTimelineViewBryntumInstances"
import useJobTimelineViewStates from "./useJobTimelineViewStates"

type ResourcesEventsMap = {
    [resourceId: string]: EventModel<CalendarEvent>[]
}

export default function useJobTimelineViewOverlappingUtils() {
    const { schedulerPro } = useJobTimelineViewBryntumInstances()
    const { timelineViewOrientation } = useJobTimelineViewStates()

    const isOverlapping = (
        event: EventModel<CalendarEvent>,
        nextEvent: EventModel<CalendarEvent>,
        currentOverlap: OverlappingRegion | null,
    ) => {
        return (
            (nextEvent && event.endDate > nextEvent.startDate) ||
            (currentOverlap && (event.endDate > currentOverlap.startDate || currentOverlap.endDate > event.startDate))
        )
    }

    const updateOverlapRegion = (
        currentOverlap: OverlappingRegion | null,
        event: EventModel<CalendarEvent>,
        resourceId: string,
    ): OverlappingRegion => {
        if (!currentOverlap) {
            return {
                startDate: event.startDate,
                endDate: event.endDate,
                resourceId,
                amountOfItems: 1,
            } as OverlappingRegion
        }
        if (event.endDate > currentOverlap.endDate) {
            currentOverlap.endDate = new Date(event.endDate)
        }
        if (currentOverlap.amountOfItems !== undefined) {
            currentOverlap.amountOfItems += 1
        } else {
            currentOverlap.amountOfItems = 1
        }
        return currentOverlap
    }

    const isLastOverlappingEvent = (nextEvent: EventModel<CalendarEvent>, currentOverlap: OverlappingRegion) => {
        return !nextEvent || nextEvent.startDate >= currentOverlap.endDate
    }

    const finalizeCurrentOverlap = (overlap: OverlappingRegion) => {
        overlap.cls = `job-timeline-view__overlapping-region`
        overlap.id = `${overlap.startDate.toString()}-${overlap.resourceId}`
        return overlap
    }

    const findOverlappingRegions = (resourcesEventsMap: { [resourceId: string]: EventModel<CalendarEvent>[] }) => {
        const overlappingRegions: OverlappingRegion[] = []
        Object.entries(resourcesEventsMap).forEach(([resourceId, events]) => {
            events.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())

            let currentOverlap: OverlappingRegion | null = null
            events.forEach((event, index, arr) => {
                const nextEvent = arr[index + 1]
                if (isOverlapping(event, nextEvent, currentOverlap)) {
                    currentOverlap = updateOverlapRegion(currentOverlap, event, resourceId)
                    if (isLastOverlappingEvent(nextEvent, currentOverlap)) {
                        overlappingRegions.push(finalizeCurrentOverlap(currentOverlap))
                        currentOverlap = null
                    }
                }
            })
        })

        return overlappingRegions
    }

    const addOverlappingRegionToScheduler = (region: OverlappingRegion, scheduler: JobTimelineScheduler) => {
        const overlappingRegion: OverlappingRegion & { id: string } = {
            ...region,
            id: `${region.startDate.toString()}-${region.resourceId}`,
        }
        const counter = {
            ...region,
            cls: `job-timeline-view__overlapping-region--counter`,
            id: `${region.startDate.toString()}-${region.resourceId}-counter`,
            isOverlappingTimeRange: true,
            amountOfItems: region.amountOfItems,
        }

        if (!scheduler.resourceTimeRangeStore.getById(overlappingRegion.id)) {
            scheduler.resourceTimeRangeStore.add(overlappingRegion)
            scheduler.resourceTimeRangeStore.add(counter)
        }

        scheduler.refreshRows()
    }

    const queryOverlappingRegions = (scheduler: JobTimelineScheduler): ResourceTimeRangeModel[] => {
        return scheduler.resourceTimeRangeStore.query((timeRange: ResourceTimeRangeModel) => {
            const classes = timeRange?.cls
            return typeof classes === "string"
                ? classes.includes("job-timeline-view__overlapping-region")
                : (classes as DomClassList).contains("job-timeline-view__overlapping-region")
        }) as ResourceTimeRangeModel[]
    }

    const queryOverlappingCounters = (scheduler: JobTimelineScheduler): ResourceTimeRangeModel[] => {
        return scheduler.resourceTimeRangeStore.query((timeRange: ResourceTimeRangeModel) => {
            const classes = timeRange?.cls
            return typeof classes === "string"
                ? classes.includes("job-timeline-view__overlapping-region--counter")
                : (classes as DomClassList).contains("job-timeline-view__overlapping-region--counter")
        }) as ResourceTimeRangeModel[]
    }

    const collectAffectedTechnicianIds = (
        resourcesEventsMap: ResourcesEventsMap,
        existingOverlappingRegions: ResourceTimeRangeModel[],
    ) => {
        const affectedTechnicianIds = new Set(Object.keys(resourcesEventsMap))

        existingOverlappingRegions.forEach((region) => {
            const resourceId = (region as unknown as { resourceId: string }).resourceId
            if (typeof resourceId === "string") {
                affectedTechnicianIds.add(resourceId)
            }
        })

        return affectedTechnicianIds
    }

    const removeExistingCounters = (
        scheduler: JobTimelineScheduler,
        existingOverlappingCounters: ResourceTimeRangeModel[],
    ) => {
        existingOverlappingCounters.forEach((counter) => {
            scheduler.resourceTimeRangeStore.remove(counter)
        })
    }

    const filterExistingRegionsForTechnician = (
        existingOverlappingRegions: ResourceTimeRangeModel[],
        resourceId: string,
    ) => {
        return existingOverlappingRegions.filter(
            (region) => (region as unknown as { resourceId: string }).resourceId === resourceId,
        )
    }

    const removeExistingRegions = (
        scheduler: JobTimelineScheduler,
        existingResourceRegions: ResourceTimeRangeModel[],
    ) => {
        existingResourceRegions.forEach((region) => {
            scheduler.resourceTimeRangeStore.remove(region)
        })
    }

    const createOverlappingRegion = (newRegion: OverlappingRegion, resourceId: string) => {
        const regionId = `${newRegion.startDate.toISOString()}-${resourceId}`
        return {
            startDate: newRegion.startDate,
            endDate: newRegion.endDate,
            resourceId,
            amountOfItems: newRegion.amountOfItems,
            id: regionId,
            cls: "job-timeline-view__overlapping-region",
        }
    }

    const createOverlappingCounter = (newRegion: OverlappingRegion, resourceId: string) => {
        const regionId = `${newRegion.startDate.toISOString()}-${resourceId}`
        return {
            startDate: newRegion.startDate,
            endDate: newRegion.endDate,
            resourceId,
            amountOfItems: newRegion.amountOfItems,
            id: `${regionId}-counter`,
            cls: "job-timeline-view__overlapping-region--counter",
            isOverlappingTimeRange: true,
        }
    }

    const addOverlappingElements = (
        scheduler: JobTimelineScheduler,
        newRegion: OverlappingRegion,
        resourceId: string,
    ) => {
        const region = createOverlappingRegion(newRegion, resourceId)
        const counter = createOverlappingCounter(newRegion, resourceId)

        scheduler.resourceTimeRangeStore.add(region as unknown as ResourceTimeRangeModel)
        scheduler.resourceTimeRangeStore.add(counter as unknown as ResourceTimeRangeModel)
    }

    const processTechnicianOverlaps = (
        scheduler: JobTimelineScheduler,
        events: EventModel<CalendarEvent>[],
        technicianId: string,
        existingOverlappingRegions: ResourceTimeRangeModel[],
    ) => {
        const existingTechnicianOverlappingRegions = filterExistingRegionsForTechnician(
            existingOverlappingRegions,
            technicianId,
        )

        const noOverlapsPossible = events.length < 2
        if (noOverlapsPossible) {
            removeExistingRegions(scheduler, existingTechnicianOverlappingRegions)
            return
        }

        const overlappingRegions = findOverlappingRegionsForTechnician(events, technicianId)
        removeExistingRegions(scheduler, existingTechnicianOverlappingRegions)

        overlappingRegions.forEach((newRegion) => {
            addOverlappingElements(scheduler, newRegion, technicianId)
        })
    }

    const handleOverlappingEvents = () => {
        if (!schedulerPro.current) {
            return
        }

        const scheduler = schedulerPro.current.instance

        if (timelineViewOrientation === "vertical") {
            const resourcesEventsMap = groupEventsByTechnician(scheduler.eventStore)

            const overlappingRegions = findOverlappingRegions(resourcesEventsMap)
            overlappingRegions.forEach((region) => addOverlappingRegionToScheduler(region, scheduler))
        } else {
            const techniciansEventsMap = groupEventsByTechnician(scheduler.eventStore)

            const existingOverlappingRegions = queryOverlappingRegions(scheduler)
            const existingOverlappingCounters = queryOverlappingCounters(scheduler)

            const affectedTechnicianIds = collectAffectedTechnicianIds(
                techniciansEventsMap,
                existingOverlappingRegions,
            )
            removeExistingCounters(scheduler, existingOverlappingCounters)

            affectedTechnicianIds.forEach((technicianId) => {
                const events = techniciansEventsMap[technicianId] || []
                processTechnicianOverlaps(scheduler, events, technicianId, existingOverlappingRegions)
            })
        }
    }

    const findOverlappingEvents = (
        eventRecord: SchedulerEventModel,
        resourceRecord: SchedulerResourceModel<CalendarTechnician>,
        startDate: Date,
        endDate: Date,
    ) => {
        if (!schedulerPro.current) {
            return []
        }

        return schedulerPro.current.instance.eventStore.query((record: SchedulerEventModel) => {
            return (
                record !== eventRecord &&
                record.resourceId === resourceRecord.id &&
                record.startDate < endDate &&
                record.endDate > startDate
            )
        })
    }

    const sortEventsByStartDate = (events: EventModel<CalendarEvent>[]) => {
        return events.sort((a, b) => {
            const aStartDate = new Date(a.startDate)
            const bStartDate = new Date(b.startDate)
            return aStartDate.getTime() - bStartDate.getTime()
        })
    }

    const findOverlappingEventsForEvent = (events: EventModel<CalendarEvent>[], currentIndex: number) => {
        const currentEvent = events[currentIndex]
        const currentStart = new Date(currentEvent.startDate)
        const currentEnd = new Date(currentEvent.endDate)

        return events.filter((event, index) => {
            if (index === currentIndex) {
                return false
            }
            const eventStart = new Date(event.startDate)
            const eventEnd = new Date(event.endDate)
            return eventStart < currentEnd && eventEnd > currentStart
        })
    }

    const findOverlappingRegionsForTechnician = (
        events: EventModel<CalendarEvent>[],
        technicianId: string,
    ): OverlappingRegion[] => {
        const regions: OverlappingRegion[] = []
        if (events.length < 2) {
            return regions
        }

        const sortedEvents = sortEventsByStartDate(events)

        for (let i = 0; i < sortedEvents.length; i++) {
            const currentEvent = sortedEvents[i]
            const currentStart = new Date(currentEvent.startDate)
            const currentEnd = new Date(currentEvent.endDate)

            const overlappingEvents = findOverlappingEventsForEvent(sortedEvents, i)

            if (overlappingEvents.length > 0) {
                const overlapCount = overlappingEvents.length + 1
                let regionStart = currentStart
                let regionEnd = currentEnd

                overlappingEvents.forEach((event) => {
                    const eventStart = new Date(event.startDate)
                    const eventEnd = new Date(event.endDate)
                    if (eventStart < regionStart) {
                        regionStart = eventStart
                    }
                    if (eventEnd > regionEnd) {
                        regionEnd = eventEnd
                    }
                })

                const existingOverlap = regions.find(
                    (region) => region.startDate < regionEnd && region.endDate > regionStart,
                )

                if (existingOverlap) {
                    existingOverlap.amountOfItems = Math.max(existingOverlap.amountOfItems || 0, overlapCount)
                    existingOverlap.startDate = new Date(
                        Math.min(regionStart.getTime(), existingOverlap.startDate.getTime()),
                    )
                    existingOverlap.endDate = new Date(
                        Math.max(regionEnd.getTime(), existingOverlap.endDate.getTime()),
                    )
                } else {
                    regions.push({
                        startDate: regionStart,
                        endDate: regionEnd,
                        resourceId: technicianId,
                        amountOfItems: overlapCount,
                    })
                }
            }
        }

        return regions
    }

    const groupEventsByTechnician = (eventStore: EventStore) => {
        const techniciansEventsMap: ResourcesEventsMap = {}

        const initializeTechnicianArrays = (event: EventModel<CalendarEvent>) => {
            if (event.resourceId) {
                techniciansEventsMap[event.resourceId] = techniciansEventsMap[event.resourceId] || []
            }

            if (event._data.assigned_technicians) {
                event._data.assigned_technicians.forEach((technician: CalendarTechnician) => {
                    techniciansEventsMap[technician.id] = techniciansEventsMap[technician.id] || []
                })
            }
        }

        const populateTechnicianArrays = (event: EventModel<CalendarEvent>) => {
            const { resourceId } = event
            if (!resourceId) {
                return
            }

            const assigned = event._data?.assigned_technicians ?? []
            const eventIsMultiAssigned = assigned.length > 1
            if (eventIsMultiAssigned) {
                assigned.forEach((technician: CalendarTechnician) => {
                    techniciansEventsMap[technician.id].push(event)
                })
            } else {
                techniciansEventsMap[resourceId].push(event)
            }
        }

        eventStore.forEach(initializeTechnicianArrays)
        eventStore.forEach(populateTechnicianArrays)

        Object.values(techniciansEventsMap).forEach(sortEventsByStartDate)

        return techniciansEventsMap
    }

    const removeAllOverlappingRegionFeedback = () => {
        if (!schedulerPro.current) {
            return
        }

        const scheduler = schedulerPro.current.instance

        const items = scheduler.resourceTimeRangeStore.query((timeRange: EventModel<CalendarEvent>) => {
            const classes = timeRange.cls as DomClassList

            return (
                classes.contains("job-timeline-view__overlapping-region") ||
                classes.contains("job-timeline-view__overlapping-region--counter")
            )
        })

        scheduler.resourceTimeRangeStore.remove(items)
    }

    return {
        handleOverlappingEvents,
        removeAllOverlappingRegionFeedback,
        findOverlappingEvents,
    }
}
